import { intlLocale } from './locales.js';
/** All business dates are shown in Sri Lanka time. */
export const DEFAULT_TIME_ZONE = 'Asia/Colombo';
const numberFormats = new Map();
const dateFormats = new Map();
function numberFormat(locale, options) {
    const cacheKey = `${locale}|${JSON.stringify(options)}`;
    let fmt = numberFormats.get(cacheKey);
    if (!fmt) {
        fmt = new Intl.NumberFormat(intlLocale(locale), options);
        numberFormats.set(cacheKey, fmt);
    }
    return fmt;
}
function dateFormat(locale, options) {
    const cacheKey = `${locale}|${JSON.stringify(options)}`;
    let fmt = dateFormats.get(cacheKey);
    if (!fmt) {
        fmt = new Intl.DateTimeFormat(intlLocale(locale), options);
        dateFormats.set(cacheKey, fmt);
    }
    return fmt;
}
/**
 * Hermes' Intl.NumberFormat.format() throws "Cannot convert BigInt to number" for bigint
 * input, so values inside the safe-integer range go in as numbers; larger ones go in as an
 * exact decimal string (the Intl spec's string form), which engines with full support keep exact.
 */
function intlInput(value) {
    if (typeof value === 'number')
        return value;
    const safe = value >= BigInt(Number.MIN_SAFE_INTEGER) && value <= BigInt(Number.MAX_SAFE_INTEGER);
    return safe ? Number(value) : value.toString();
}
function toBigInt(value, name) {
    if (typeof value === 'bigint')
        return value;
    if (!Number.isSafeInteger(value)) {
        throw new RangeError(`${name} must be a safe integer, got ${String(value)}`);
    }
    return BigInt(value);
}
/**
 * Convert integer minor units (cents) to an exact decimal string, e.g. -12345n → "-123.45".
 * Avoids floating-point division so large amounts format exactly.
 */
export function minorToDecimalString(amountMinor) {
    const v = toBigInt(amountMinor, 'amountMinor');
    const abs = v < 0n ? -v : v;
    const major = abs / 100n;
    const cents = (abs % 100n).toString().padStart(2, '0');
    return `${v < 0n ? '-' : ''}${major}.${cents}`;
}
/** Format an LKR amount given in integer cents (Req 22.4). */
export function formatLkr(amountMinor, locale, options = {}) {
    const decimal = minorToDecimalString(amountMinor);
    const whole = decimal.endsWith('.00');
    const digits = options.trimWholeCents && whole ? 0 : 2;
    return numberFormat(locale, {
        style: 'currency',
        currency: 'LKR',
        currencyDisplay: options.currencyDisplay ?? 'symbol',
        minimumFractionDigits: digits,
        maximumFractionDigits: 2,
    }).format(decimal);
}
/** Locale-aware number formatting with grouping (e.g. ta-LK uses lakh grouping). */
export function formatNumber(value, locale, options = {}) {
    return numberFormat(locale, options).format(intlInput(value));
}
/** Format an integer point amount. */
export function formatPoints(points, locale) {
    return numberFormat(locale, { maximumFractionDigits: 0 }).format(intlInput(toBigInt(points, 'points')));
}
const pluralRules = new Map();
function getPluralRules(locale) {
    let r = pluralRules.get(locale);
    if (!r) {
        r = new Intl.PluralRules(intlLocale(locale));
        pluralRules.set(locale, r);
    }
    return r;
}
/**
 * The CLDR cardinal plural category ("one", "other", ...) for an exact integer point
 * amount. Works for values beyond `Number.MAX_SAFE_INTEGER`: for cardinals in every
 * supported locale only the magnitude-1 case is special, so a value whose absolute value
 * is not 1n is selected via a safe representative, keeping the category exact without ever
 * converting a large bigint to a lossy `number`. Returned as the string an ICU `select`
 * branch matches, so a message can show a separately pre-formatted exact number.
 */
export function pluralCategory(value, locale) {
    const v = toBigInt(value, 'value');
    const abs = v < 0n ? -v : v;
    // Safe integers go in exactly; larger ones use a representative >1 (category "other" in
    // en/si/ta). 0 and 1 keep their own category.
    const representative = abs <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(abs) : 1000;
    return getPluralRules(locale).select(representative);
}
/** Format a percentage stored as basis points (e.g. tier `discount_bps` 250 → "2.5%"). */
export function formatPercentBps(bps, locale) {
    if (!Number.isSafeInteger(bps))
        throw new RangeError(`bps must be a safe integer, got ${String(bps)}`);
    return numberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }).format(bps / 10_000);
}
function toDate(value) {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime()))
        throw new RangeError(`Invalid date: ${String(value)}`);
    return d;
}
/** Format a calendar date in Asia/Colombo by default. */
export function formatDate(value, locale, options = { dateStyle: 'medium' }) {
    return dateFormat(locale, { timeZone: DEFAULT_TIME_ZONE, ...options }).format(toDate(value));
}
/** Format a date and time in Asia/Colombo by default. */
export function formatDateTime(value, locale, options = { dateStyle: 'medium', timeStyle: 'short' }) {
    return dateFormat(locale, { timeZone: DEFAULT_TIME_ZONE, ...options }).format(toDate(value));
}
//# sourceMappingURL=format.js.map